import {
  ConsolidationPriority,
  ConsolidationPriorityMode,
  ConsolidationPriorityStatus,
} from "@/models/consolidation";

export const DOC_TYPE_LABELS: Record<number, string> = {
  10: "Lov",
  20: "Ændringslov",
  30: "LBK",
  60: "BEK",
  80: "Ændringsbek.",
};

export const MODE_LABELS: Record<ConsolidationPriorityMode, string> = {
  WINDOW: "Kun vinduet",
  FORWARD: "Fremadrettet",
  UP_TO: "Til og med",
  FULL: "Hele tidslinjen",
};

export const MODE_DESCRIPTIONS: Record<ConsolidationPriorityMode, string> = {
  WINDOW:
    "Konsoliderer ændringslovene i det vindue (LBK → LBK), dokumentet tilhører.",
  FORWARD:
    "Dokumentets vindue og alle senere – også ændringslove der udgives fremover.",
  UP_TO: "Alle vinduer fra tidslinjens start til og med dokumentets vindue.",
  FULL: "Hele tidslinjen, inklusive fremtidige ændringslove og LBK'er.",
};

export type PriorityStatusDisplay = {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  detail?: string;
};

/**
 * One-line status for a prioritization, derived from its queue/review rollup.
 * Precedence: human-blocked > working > done-with-review-progress > waiting.
 */
export const getPriorityStatusDisplay = (
  status: ConsolidationPriorityStatus | null | undefined
): PriorityStatusDisplay => {
  if (!status) {
    return { label: "Afventer kø", variant: "outline" };
  }

  const total =
    status.queued +
    status.inProgress +
    status.done +
    status.awaitingAction +
    status.blocked +
    status.skipped;
  const detail =
    status.approvedChangeLaws + status.needsReviewChangeLaws > 0
      ? `${status.approvedChangeLaws} godkendt · ${status.needsReviewChangeLaws} til gennemsyn`
      : undefined;

  if (status.blocked > 0 || status.awaitingAction > 0) {
    return {
      label: `Afventer handling (${status.blocked + status.awaitingAction})`,
      variant: "destructive",
      detail,
    };
  }
  if (status.inProgress > 0 || status.queued > 0) {
    return {
      label: `Konsoliderer… ${status.done}/${total}`,
      variant: "secondary",
      detail,
    };
  }
  if (status.done > 0) {
    return { label: "Konsolideret", variant: "default", detail };
  }
  return { label: "Afventer kø", variant: "outline", detail };
};

export const getPriorityRowKey = (priority: ConsolidationPriority): string =>
  priority.id;
